/* eslint-disable consistent-return */
import { useEffect, useContext, useRef } from 'react';
import { AuthContext } from 'src/auth/context';
import {
  ensureClientConnected,
  joinUserRoom,
  getClientSocket,
  clientAuthSync,
  reconnectClientSocket,
} from 'src/utils/socket';
import { rejectToast, resolveToast } from 'src/utils/toastFlow';

import { pricesUpdated } from 'src/redux/slices/prices-slice';
import { contractBalanceUpdated } from 'src/redux/slices/contract-balance-slice';
import {
  prependTransaction,
  updateTransaction,
  updateUserTransactions,
} from 'src/redux/slices/transactions-slice';
import { clientDispatch } from 'src/redux/clientStore';
import { totalVolumeUsdSet } from 'src/redux/slices/client-profit-slice';

// GLITCH FIXES (vs previous version):
// - Removed the 2s "duplicate" filter on updateTransaction:update. It dropped
//   real status changes (deposit_seen → deposit_confirmed arriving <2s apart),
//   leaving the UI stuck on an old status until a refresh. The reducer merges
//   by _id, so replays are harmless.
// - Removed the 300ms throttles on prices / balances / transactions / funds.
//   They dropped the LATEST value of a burst, so the UI showed stale data, and
//   funds:send for two different intents could swallow one toast.
// - Room join is now driven by one serialized sync (socket.js coalesces
//   auth:sync), instead of three overlapping auth:sync calls per connect.
// - Listeners are attached once per socket (not per component instance) and
//   callbacks are dispatched to EVERY mounted instance, not only the first.

// ── Per-socket listener bookkeeping ───────────────────────────────────────
const attachedClientSockets = new WeakSet();

// Every mounted hook instance registers its callback refs here so a single set
// of socket listeners can fan out to all of them.
const subscribers = new Set();

function notifySubscribers(key, payload) {
  subscribers.forEach((refs) => {
    const fn = refs[key]?.current;
    if (typeof fn === 'function') {
      try {
        fn(payload);
      } catch (error) {
        console.error(`[useClientSocket] ${key} callback failed`, error);
      }
    }
  });
}

// Latest auth snapshot (shared by all instances — there is one session).
const authState = {
  isUserAuthenticated: false,
  userId: null,
};

function getTxUserId(transaction) {
  const raw = transaction?.userId;

  if (raw && typeof raw === 'object') {
    return String(raw._id || '');
  }

  return String(raw || '');
}

// ── Room sync ─────────────────────────────────────────────────────────────
let syncRunning = false;
let syncAgain = false;
let mismatchReconnects = 0;
let mismatchUserId = '';

async function syncUserRoomOnce() {
  const { isUserAuthenticated, userId } = authState;

  const res = await clientAuthSync();

  if (!res?.ok) {
    if (!res?.cancelled) {
      console.warn('❌ auth:sync failed — skipping room join:', res);
    }
    return;
  }

  if (!isUserAuthenticated || !userId) return;

  // A new login gets its own reconnect allowance.
  if (mismatchUserId !== String(userId)) {
    mismatchUserId = String(userId);
    mismatchReconnects = 0;
  }

  // The socket's handshake still carries an older session (e.g. the wallet
  // claim / login rotated the cookie after the socket connected): the server
  // answers with no userId or a different one. A fresh connection picks up
  // the new cookie — the server only lets a socket join its own session's
  // room. Bounded so a backend mismatch can never cause a reconnect loop.
  if (String(res.userId || '') !== String(userId)) {
    if (mismatchReconnects >= 1) {
      console.warn('❌ auth:sync userId mismatch persists — skipping room join', {
        frontendUserId: userId,
        socketUserId: res.userId,
      });
      return;
    }

    mismatchReconnects += 1;
    await reconnectClientSocket();

    // The fresh handshake carries the current session cookie; confirm the
    // server now sees this user before joining.
    const after = await clientAuthSync();

    if (String(after?.userId || '') !== String(userId)) {
      console.warn('❌ socket session still does not match after reconnect', {
        frontendUserId: userId,
        socketUserId: after?.userId || null,
      });
      return;
    }

    await joinUserRoom(userId);
    return;
  }

  mismatchReconnects = 0;
  await joinUserRoom(userId);
}

async function syncUserRoom() {
  if (syncRunning) {
    syncAgain = true;
    return;
  }

  syncRunning = true;

  try {
    await ensureClientConnected();
    await syncUserRoomOnce();
  } catch (error) {
    console.log('❌ client socket sync failed:', error?.message || error);
  } finally {
    syncRunning = false;
  }

  if (syncAgain) {
    syncAgain = false;
    await syncUserRoom();
  }
}

// ── Listener attachment (once per socket) ─────────────────────────────────
function attachListeners(socket) {
  if (attachedClientSockets.has(socket)) return;
  attachedClientSockets.add(socket);

  socket.on('funds:send', (payload) => {
    const { isUserAuthenticated, userId } = authState;
    if (!isUserAuthenticated || !userId) return;

    const payloadUserId = String(payload?.userId || '').toLowerCase();
    if (payloadUserId !== String(userId).toLowerCase()) return;

    if (payload?.error) {
      rejectToast(payload?.intentId);
      return;
    }

    resolveToast(payload?.intentId);
  });

  socket.on('contract-balances', (payload) => {
    clientDispatch(contractBalanceUpdated(payload));
    notifySubscribers('setContractBalance', payload);
    notifySubscribers('onBalance', payload);
  });

  socket.on('prices', (prices) => {
    clientDispatch(pricesUpdated(prices));
  });

  socket.on('transactions:update', (payload) => {
    const transactions = payload?.transactions;
    if (!Array.isArray(transactions)) return;

    clientDispatch(updateUserTransactions(transactions));
  });

  socket.on('updateTransaction:update', (payload) => {
    const transaction = payload?.transaction;
    if (!transaction?._id) return;

    clientDispatch(updateTransaction(transaction));
    notifySubscribers('onTransaction', transaction);
  });

  socket.on('transaction:created', (payload) => {
    const transaction = payload?.transaction;
    if (!transaction?._id) return;

    const { userId } = authState;
    if (!userId) return;
    if (getTxUserId(transaction) !== String(userId)) return;

    // prependTransaction upserts by _id, so a replayed event is harmless.
    clientDispatch(prependTransaction(transaction));
    notifySubscribers('onTransaction', transaction);
  });

  socket.on('transactionVolume:update', (payload) => {
    let volume = payload;
    if (payload?.volume) {
      volume = payload.volume;
    }

    const volumeUsd = volume?.allTime?.volumeUsd;
    if (volumeUsd === null || volumeUsd === undefined) return;

    clientDispatch(totalVolumeUsdSet(volumeUsd));
  });
}

export function useClientSocket({
  setContractBalance,
  onBalance,
  onTransaction,
  watchedTxId,
  autoJoin = true,
  autoConnect = true,
  autoAuthSync = true,
} = {}) {
  const { isUserAuthenticated, user } = useContext(AuthContext);

  let userId = null;
  if (user?._id || user?.id) {
    userId = String(user._id || user.id);
  }

  const sessionAuthType = user?.sessionAuthType || null;
  const sessionAuthVersion = user?.sessionAuthVersion || null;

  const setContractBalanceRef = useRef(setContractBalance);
  const onBalanceRef = useRef(onBalance);
  const onTransactionRef = useRef(onTransaction);
  const watchedTxIdRef = useRef(watchedTxId);

  // Keep callback refs current without re-attaching listeners.
  useEffect(() => {
    setContractBalanceRef.current = setContractBalance;
    onBalanceRef.current = onBalance;
    onTransactionRef.current = onTransaction;
    watchedTxIdRef.current = watchedTxId;
  });

  // Register this instance's callbacks with the shared listeners.
  useEffect(() => {
    const refs = {
      setContractBalance: setContractBalanceRef,
      onBalance: onBalanceRef,
      onTransaction: onTransactionRef,
    };

    subscribers.add(refs);

    return () => {
      subscribers.delete(refs);
    };
  }, []);

  // Attach socket listeners once and open the connection.
  useEffect(() => {
    const socket = getClientSocket();
    attachListeners(socket);

    if (!autoConnect) return;

    ensureClientConnected().catch((error) => {
      console.log('❌ client socket connect failed:', error?.message || error);
    });
  }, [autoConnect]);

  // Session changed (login, logout, wallet claim) → resync rooms.
  useEffect(() => {
    authState.isUserAuthenticated = Boolean(isUserAuthenticated);
    authState.userId = userId;

    if (!autoAuthSync || !autoJoin) return;

    syncUserRoom();
  }, [autoAuthSync, autoJoin, isUserAuthenticated, userId, sessionAuthType, sessionAuthVersion]);
}

export default useClientSocket;
